/**
 * ModeSelector Component
 *
 * Compact inline dropdown in the header for quick mode switching.
 * Shows the active mode name or "Custom" when no mode matches.
 */

import { Layers, ChevronDown, Settings2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { ImageMode } from '../types/image-generator-modes';

export interface ModeSelectorProps {
  activeMode: ImageMode | null;
  modes: ImageMode[];
  onSelectMode: (mode: ImageMode) => void;
  onOpenModes: () => void;
}

export function ModeSelector({ activeMode, modes, onSelectMode, onOpenModes }: ModeSelectorProps) {
  const builtInModes = modes.filter((m) => m.builtIn);
  const customModes = modes.filter((m) => !m.builtIn);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-8 gap-1.5 text-xs max-w-[200px]"
          title={activeMode ? `Current mode: ${activeMode.name}` : 'No mode active (Custom)'}
        >
          <Layers className="h-4 w-4 shrink-0" />
          <span className="truncate hidden sm:inline">{activeMode?.name ?? 'Custom'}</span>
          <ChevronDown className="h-3 w-3 shrink-0 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64 max-h-80 overflow-y-auto">
        <DropdownMenuItem onClick={onOpenModes} className="cursor-pointer gap-2">
          <Settings2 className="h-4 w-4" />
          <span className="font-medium text-sm">Manage Modes</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-xs text-muted-foreground">Built-in</DropdownMenuLabel>
        {builtInModes.map((mode) => (
          <DropdownMenuItem
            key={mode.id}
            onClick={() => onSelectMode(mode)}
            className="flex flex-col items-start gap-0 cursor-pointer"
          >
            <span className="font-medium text-sm">{mode.name}</span>
            <span className="text-xs text-muted-foreground line-clamp-1">{mode.description}</span>
          </DropdownMenuItem>
        ))}

        {customModes.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs text-muted-foreground">Custom</DropdownMenuLabel>
            {customModes.map((mode) => (
              <DropdownMenuItem
                key={mode.id}
                onClick={() => onSelectMode(mode)}
                className="flex flex-col items-start gap-0 cursor-pointer"
              >
                <span className="font-medium text-sm">{mode.name}</span>
                {mode.description && (
                  <span className="text-xs text-muted-foreground line-clamp-1">
                    {mode.description}
                  </span>
                )}
              </DropdownMenuItem>
            ))}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
